/**
 * filter-params.js
 *
 * Convenience functions for filter parameter manipulation. Filter params are similar to query
 * params but custom-formatted for front-end router URLs. ex: instead of "/my/path?var1=foo&var2=bar"
 * you could use filter params in this form "my/path/var1=foo;var2=bar/morepath".
 */

import { lightFormat, parseISO } from 'date-fns';
import { Types } from "./constants";


const paramSeparator = ';';
const arraySeparator = '|';


// converts a given value to a url-safe string.
// if array, converts to [v1|v2|v3] syntax.
// if bool, converts to 1 or 0.
// otherwise uses value as is.
const convertValueToString = (val) => {
  if (Array.isArray(val)) {
    return `[${val.map(v => convertValueToString(v)).join(arraySeparator)}]`;
  } else if (typeof val == 'boolean') {
    return val ? '1' : '0';
  } else {
    return val;
  }
}


// Takes an object of key value pairs, ex: {complete: 1, workMode: 1} and converts
// to a safe matrix params string to use in the URL path. ex: "complete=1;workMode=1".
// arrays are converted into a | separated string as per separator definition
export const getFilterParamsString = (filterParams) => {
  const urlSearchParams = new URLSearchParams();
  for (let [key, value] of Object.entries(filterParams)) {
    urlSearchParams.append(key, convertValueToString(value));
  }
  return urlSearchParams.toString().replace(/&/g, paramSeparator);
};


// Returns an object based on the router params in the provided URL segment
export const getFilterParamsObj = (filterParamsString) => {
  return filterParamsString ? filterParamsString.split(paramSeparator)
    .reduce(function (acc, curr) {
      var keyValuePair = curr.split("=");
      if (keyValuePair.length > 1) {
        keyValuePair[1] = keyValuePair[1].replace(/\+/g, '%20');
        keyValuePair[1] = decodeURIComponent(keyValuePair[1]);

        // array type
        if (keyValuePair[1].startsWith('[') && keyValuePair[1].endsWith(']')) {
          let value = keyValuePair[1];
          value = value.substring(1, value.length - 1).split(arraySeparator);
          acc[keyValuePair[0]] = value;
        } 
        // primitive type
        else {
          acc[keyValuePair[0]] = keyValuePair[1];
        }
      }
      return acc;
    }, {}) : {};
};

// Get a standalone "summary" for a given param/value, suitable for use e.g.
// in a compact list of applied parameters. By default, this will just be the
// text representation of the value itself, but that isn't always approproate
// or possible.
export const getParamSummary = (param, value, filterParams, searchOptions, criteriaSpecs, t) => {

  if (param === 'recordGroups') {
    // Record Groups is a 1-value array for compatibility with the back end
    return {label: 'Record Type', value: Types.RecordGroup.getName(t, value[0])};
  } else if (param === 'dogId') {
    // There should only be a dog param if there is one and only one handler,
    // which is either the user or a single selected managed handler.
    if (searchOptions) {
      const handlers = filterParams.handlers?.length === 1
        ? searchOptions.handlerDogs.filter(h => filterParams.handlers.includes(`${h.id}`))
        : [searchOptions.handlerDogs[0]];
      for (const handler of handlers) {
        for (const dog of handler.dogs) {
          if (`${dog.id}` === value) {
            return {label: 'Dog', value: dog.name};
          }
        }
      }
    }
    return {value: 'Dog'};
  } else if (param === 'handlers') {
    // If a single handler is selected, return the name
    if (value.length === 1) {
      const handler = searchOptions?.managedHandlers?.find(h => `${h.id}` === value[0]);
      if (handler) {
        return {label: 'Handler', value: handler.name};
      }
    }
    return {value: 'Handlers'};
  } else if (param === 'date') {
    return {label: 'Date', value: lightFormat(parseISO(value), 'MM/dd/yyyy')};
  } else if (param === 'hasTrack') {
    return {value: 'Recent Tracks'};
  } else if (['startDate', 'endDate'].includes(param)) {
    // Skip start or end date...date range will also be set to custom
    return null;
  } else if (param === 'workMode') {
    return { label: 'Work Mode', value: Types['WorkMode'].getName(t, value)};
  } else if (param === 'patrolType') {
    return { label: 'Patrol Type', value: Types['Patrol'].getName(t, value) };
  } else if (param === 'dateRange') {
    return { label: 'Date Range', value: Types['DateRange'].getName(t, value) };
  } else {
    // The param might refer to a criteria spec
    const spec = criteriaSpecs.find((spec) => spec.type === param);
    if (!spec) {
      // Can't find crit spec
      // TODO: this shouldn't happen. Log error?
      return null;
    }

    if (spec.isSecondaryField) {
      // Secondary field: skip
      return null;
    } else if (spec.fieldType === Types.FilterCriteriaField.TEXT) {
      // Text field: return value in quotes
      return { label: spec.title, value: `"${value}"`};
    } else if (spec.fieldType === Types.FilterCriteriaField.TAG) {
      return {value: 'Tags'} // TODO: better?
    } else if (spec.isBoolean) {
      // Boolean: return title... with "(No)" if false
      return {value: `${spec.title}${value !== '1' ? ' (No)' : ''}`};
    } else {
      // Search for the value by ID in the list of the spec's values
      const name = spec.values.find((specVal) => specVal.id.toString() === value)?.val;
      if (name) {
        return {label: spec.title, value: name};
      }
    }
  }
  
  // Can't get a summary
  // TODO: this shouldn't happen. Log error?
  return null;
};